import { useEffect, useState, useMemo } from "react";
import {
  Box, Grid2 as Grid, Modal, Typography, TextField,
  RadioGroup, FormControlLabel, FormControl, FormLabel,
  Radio, Button, IconButton, InputAdornment, FormHelperText
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { grey } from "@mui/material/colors";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";
import dayjs from "dayjs";

import FilePreviewer from "../common/FilePreview";
import { isEmpty } from "../common/helpers";
import { editInversion, getUserbyId } from "../../actions/users";
import { sendEmailApproval } from "../../actions/investments";

import { useDispatch } from "react-redux";

const PaymentModal = props => {
  const dispatch = useDispatch();
  const { cancelCallback, item, visible } = props;
  const [values, setValues] = useState({
    monto: 0,
    status: "1",
    desc: '',
    concepto: ''
  });
  const [errors, setErrors] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  useEffect(() => {
    setSelectedFileIndex(0); // Reset index on item change
    if (!isEmpty(item)) {
      console.log(item);
      setValues({
        ...values,
        status: item.status,
        monto: item.monto,
        desc: item.desc,
        concepto: item.concepto || ''
      });
      if (item.fechaTransaccion && moment(item.fechaTransaccion, moment.ISO_8601, true).isValid()) {
        const parsedDate = dayjs(item.fechaTransaccion); // Convertir directamente con dayjs
        setSelectedDate(parsedDate);
      }
    }
  }, [item]);

  const fileUrl = item?.file && Array.isArray(item.file) ? item.file[selectedFileIndex] : item?.file;

  const filePreview = useMemo(() => {
    return <FilePreviewer file={fileUrl} />;
  }, [fileUrl]);

  const saveData = async e => {
    e.preventDefault();
    if (values.status === 0) {
      return setErrors({ status: "Debe de elegir un estado" });
    }
    if (values.status.toString() === "1" && (values.monto.toString() === "0" || isEmpty(values.monto))) {
      if (isEmpty(selectedDate)) {
        return setErrors({ fecha: "Debe colocar una fecha" });
      }
      return setErrors({ monto: "Debe de colocar un monto" });
    }
    if (values.status.toString() === "2" && isEmpty(values.desc)) {
      return setErrors({ desc: "Debe de colocar un porqué" });
    }
    const info = {
      date: item.date,
      fechaTransaccion: moment(selectedDate.$d).toISOString(),
      file: item.file,
      monto: values.monto,
      reference: item.reference,
      status: values.status,
      desc: values.desc,
      concepto: values.concepto
    }
    const user = await getUserbyId(item.usrId);
    const emailInfo = {
      email: user.email,
      nombre: user.name,
      subject: values.status === "1" ? "aprobacion" : values.status === "2" ? "rechazo" : "aprobacion",
      servicio: item.servicioEmpresa ? true : false,
    };
    await dispatch(editInversion(info, item.usrId, item.id));
    await sendEmailApproval(emailInfo);
    cancelCallback();
  }

  const cambiarStatus = e => {
    const { value } = e.target
    setValues({
      ...values,
      status: value,
      monto: value === "1" ? values.monto : 0,
      desc: value === "1" ? '' : values.desc
    });
  }

  const cambiarMonto = e => {
    const { value } = e.target
    setValues({
      ...values,
      monto: value,
    });
  }

  const cambiarDesc = e => {
    const { value } = e.target
    setValues({
      ...values,
      desc: value
    });
  }

  const cambiarFecha = newValue => {
    setSelectedDate(newValue);
    setErrors({
      ...errors,
      fecha: null
    })
  }

  const cambiarConcepto = e => {
    const { value } = e.target
    setValues({
      ...values,
      concepto: value,
    });
  }

  return (
    <Modal
      open={visible}
      className="modal-content"
      onClose={() => {
        setErrors({});
        cancelCallback();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="modal-general modal-large">
        <IconButton
          sx={{
            position: 'relative',
            top: -30,
            left: "97%",
            color: 'grey.600',
            '&:hover': {
              backgroundColor: 'grey.100',
            },
          }}
          onClick={() => {
            cancelCallback();
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
        <Typography className="titleMain" variant="h6" component="h6">
          Visualizar comprobante de pago
        </Typography>
        <Typography className="subtitleMain">
          Visualiza el monto y aprueba la operación
        </Typography>
        <Grid container spacing={3} mt={5}>
          <Grid
            size={{ md: 6, lg: 8 }}
            sx={{ overflowY: "auto", maxHeight: '65vh' }}
            className="file-preview">
            {item?.file && Array.isArray(item.file) && item.file.length > 1 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {item.file.map((_, index) => (
                  <Button
                    key={index}
                    variant={selectedFileIndex === index ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setSelectedFileIndex(index)}
                  >
                    Archivo {index + 1}
                  </Button>
                ))}
              </Box>
            )}
            {filePreview}
          </Grid>
          <Grid className="description" size={{ md: 6, lg: 4 }}>
            <Typography className="titleMain" variant="h6" component="h6">
              Instrucciones
            </Typography>
            <Typography className="text-modal" mt={1}>
              Por favor, revisa el documento adjunto que se encuentra del lado izquierdo.
            </Typography>
            <ol className="list-items">
              <li>Identifica el monto de inversión correspondiente.</li>
              <li>Si todo es correcto, selecciona aprobar e ingresa el monto en el campo y selecciona "Aprobado".</li>
              <li>Si encuentras alguna inconsistencia, el archivo no existe o no puedes verificar la información, selecciona la opción "No aprobar"</li>
            </ol>
            <form noValidate onSubmit={saveData}>
              <Grid className="form-modal" size={12} mt={3}>
                {
                  item?.status.toString() === "2" ? (
                    <Typography color="error">
                      No Aprobado
                    </Typography>
                  ) : (
                    <FormControl
                      error={errors.status === 0 ? false : true}
                    >
                      <FormLabel id="status">Selecciona:</FormLabel>
                      <RadioGroup
                        aria-labelledby="status"
                        defaultValue={1}
                        name="radio-buttons-group"
                        value={values.status}
                        onChange={cambiarStatus}
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio size="small" />}
                          label="Aprobado"
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio size="small" />}
                          label="No aprobado"
                        />
                      </RadioGroup>
                      <FormHelperText>
                        {errors.status && errors.status}
                      </FormHelperText>
                    </FormControl>
                  )
                }
                <FormControl className="full-input" fullWidth sx={{ m: 1 }}>
                  {
                    values.status.toString() === "1" ? (
                      <Grid>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                          <DatePicker
                            label="Fecha de pago"
                            value={selectedDate}
                            onChange={(newValue) => cambiarFecha(newValue)}
                            format="DD-MM-YYYY"
                            fullWidth
                            sx={{ mb: 3, width: '100%' }}
                            slotProps={{
                              textField: {
                                error: !!errors.fecha,
                              },
                            }}
                          />
                        </LocalizationProvider>
                        <TextField
                          id="concepto"
                          label="Concepto"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={values.concepto}
                          onChange={cambiarConcepto}
                          sx={{ mb: 3 }}
                        />
                        <TextField
                          id="monto"
                          label="Monto de inversión"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={values.monto}
                          type="number"
                          onFocus={(e) => {
                            if (e.target.value === "0") {
                              setValues({ ...values, monto: "" });
                            }
                          }}
                          onChange={cambiarMonto}
                          error={Boolean(errors.monto)}
                          helperText={errors.monto && errors.monto}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoneyIcon
                                    sx={{ color: grey[700] }}
                                    fontSize="small"
                                  />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    ) : item?.status.toString() === "2" ? (
                      <Typography color="error">
                        {values.desc}
                      </Typography>
                    ) : (
                      <TextField
                        id="desc"
                        label="Descripción del porque no se acepto"
                        variant="outlined"
                        size="small"
                        value={values.desc}
                        onChange={cambiarDesc}
                        error={Boolean(errors.desc)}
                        helperText={errors.desc && errors.desc}
                      />
                    )
                  }
                </FormControl>
                {
                  item?.status.toString() != "2" && (
                    <Button
                      type="submit"
                      variant="contained"
                    >
                      Guardar cambios
                    </Button>
                  )
                }
              </Grid>

            </form>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  )
}

export default PaymentModal;
